import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";

import { TemplateField } from "@/shared/components/TemplateField";
import { Field, FormBlock } from "@/shared/foundation/primitives";

import { type AgentFormValues } from "../agentFormSchema";
import { useAgentVariableCatalog } from "../useAgentVariableCatalog";

import styles from "./goals.module.css";

export interface DatasetSummaryGoalFormProps {
  index: number;
}

/**
 * What a dataset-summary goal needs: nothing, unless the author wants the
 * summary shaped a particular way. Left blank, the goal handler uses its own
 * format, which is usually the right answer.
 */
export function DatasetSummaryGoalForm({ index }: DatasetSummaryGoalFormProps) {
  const { control } = useFormContext<AgentFormValues>();
  const catalog = useAgentVariableCatalog();
  const titleId = React.useId();

  return (
    <FormBlock
      titleId={titleId}
      title="Summary format hint"
      hint="How to structure the summary — bullet points, what to emphasize. Leave blank for the default format."
      className={styles.block}
      data-testid={`agent-goal-${index}-summary-format`}
    >
      <Controller
        control={control}
        name={`goals.${index}.summary_format_spec_prompt`}
        render={({ field, fieldState }) => (
          <Field.Root invalid={fieldState.invalid}>
            <TemplateField
              value={typeof field.value === "string" ? field.value : ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              catalog={catalog}
              labelledBy={titleId}
              rows={4}
              error={fieldState.invalid}
              placeholder="Bullet points, emphasize anomalies and root causes."
            />
            <Field.Error match>{fieldState.error?.message}</Field.Error>
          </Field.Root>
        )}
      />
    </FormBlock>
  );
}
